// PlateModal.tsx
import { useEffect, useRef } from "react";
import { FaAngleLeft } from "react-icons/fa";
import { Portal } from "../../portal/Portal";
import { Quantity } from "../../inputs/Quantity";
import { ComboSelector } from "../../cards/ComboSelector";
import { usePlateModal } from "../../../../hooks/usePlateModal";
import styles from "./PlateModal.module.scss";
import { useOrdersStore, useUIStore } from "../../../../store";
import { getAssetUrl } from "../../../../utils/getAssetUrl";
import { PricePlate, ProductImage } from "../../../atoms";

export const PlateModal = ({
  plate,
  onClose,
  cartItem,
  menuData,
  visible,
}: any) => {
  const {
    quantity,
    setQuantity,
    selection,
    setSelection,
    isClosing,
    setIsClosing,
    setIsLoading,
    handleStartClosing,
    handleSubmit,
    isCombo,
    isLoading,
    buttonText,
    plateCategory
  } = usePlateModal(plate, onClose, cartItem, menuData);

  const config = useUIStore((s) => s.config);
  const canUseCart = useOrdersStore((s) => s.canUseCart);
  const src = getAssetUrl(plate?.image_url, config);

  useEffect(() => {
    if (visible) {
      setIsClosing(false);
      setIsLoading(false);
      setQuantity(cartItem?.quantity ?? 1);
      if (imageWrapperRef.current) imageWrapperRef.current.style.height = "";

      if (cartItem?.comboSelection) {
        setSelection(
          Object.fromEntries(cartItem.comboSelection.map((i: any) => [i.id, i.quantity]))
        );
      } else {
        setSelection({});
      }

      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  // Scroll solo hacia abajo en el contenido
  const contentRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const dy = e.touches[0].clientY - startY;
      if (dy > 0 && el.scrollTop === 0) {
        e.preventDefault();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => {
      if (window.innerWidth >= 1024) return;
      const vh = Math.max(25, 55 - (el.scrollTop / 150) * 30);
      if (imageWrapperRef.current) imageWrapperRef.current.style.height = `${vh}vh`;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const isSelectionIncomplete =
    isCombo &&
    (() => {
      const selectItem = plate?.combo?.items?.find(
        (i: any) => i.type?.toLowerCase() === "select"
      );
      if (!selectItem) return false;
      const totalSelected = Object.values(selection || {}).reduce(
        (acc: number, val: any) => acc + Number(val), 0
      );
      return totalSelected < selectItem.quantity;
    })();

  return (
    <Portal wrapperId="plate-modal-portal">
      <div className={visible ? undefined : styles.modalHidden}>
        <div className={styles.modalOverlay} onClick={handleStartClosing}>
          <div
            className={isClosing ? styles.modalContentClosing : styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <FaAngleLeft
              onClick={handleStartClosing}
              size={36}
              className={styles.closeModal}
            />

            <div ref={imageWrapperRef} className={styles.imageWrapperOuter}>
              <ProductImage
                src={src}
                alt={plate?.title ?? ""}
                showPlaceholder={false}
                config={config}
                wrapperClassName={styles.imageWrapperInner}
                loading="eager"
              />
            </div>

            <div
              ref={contentRef}
              className={
                src
                  ? styles.containerTitleDescriptionPriceWithImage
                  : styles.containerTitleDescriptionPriceWithOutImage
              }
            >
              <div className={styles.containerTitleDescription}>
                {plateCategory && (
                  <div className={styles.categoryBadge}>
                    <span className={styles.categoryName}>{plateCategory.name}</span>
                    {plateCategory.description && (
                      <p className={styles.categoryDescription}>{plateCategory.description}</p>
                    )}
                  </div>
                )}
                <h2 className={styles.title}>{plate?.title}</h2>
                <p className={styles.fullDescription}>
                  {plate?.description?.trim() || plate?.title}
                </p>
                <PricePlate plate={plate} priceClassName={styles.bigPrice} />
              </div>

              {isCombo && (
                <ComboSelector
                  combo={plate?.combo}
                  quantity={quantity}
                  selection={selection}
                  setSelection={setSelection}
                />
              )}
            </div>

            {canUseCart && (
              <div className={styles.containerBottomBar}>
                {!isCombo && (
                  <div className={styles.containerQuantityModal}>
                    <Quantity
                      quantity={quantity || 1}
                      setQuantity={(value) => setQuantity(value < 1 ? 1 : value)}
                    />
                  </div>
                )}
                <div className={styles.addButton}>
                  <button
                    className={`${styles.buttonRequest} ${isSelectionIncomplete ? styles.buttonDisabled : ""}`}
                    onClick={handleSubmit}
                    disabled={isLoading || isSelectionIncomplete}
                  >
                    {isSelectionIncomplete ? "Falta completar selección" : buttonText}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};
